import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { snappyTween } from './ui/motionUi';
import {
  getStoredPlayerId,
  setStoredPlayerId,
  setStoredNickname,
  getStoredNickname,
  GAME_SESSION_RESET,
  GAME_CHEAT_ADD_TOKENS
} from './gamePrefs';
import {
  lookupUserState,
  patchUserState,
  fetchFlowById,
  fetchCharacters,
  fetchSets,
  fetchKinks,
  fetchItems,
  fetchCurrencyPacks,
  fetchRealItems,
  redeemPromo,
  incrementCharacterSession
} from './gameSessionApi';
import FlowPlayback from './FlowPlayback';
import {
  applyRpDeltaToCharactersAndSets,
  buildRelationshipRewardDispatchDetail
} from './relationshipRpApply';
import { buildLobbyCharacterProfiles } from './lobbyCharacterProfiles';
import GamePlayerIdModal from './ui/GamePlayerIdModal';
import GameNicknameModal from './ui/GameNicknameModal';
import GameTopBar from './ui/GameTopBar';
import GamePreviewShell from './ui/GamePreviewShell';
import GameSettingsModal from './ui/GameSettingsModal';
import GameProfileModal from './ui/GameProfileModal';
import GamePromoModal from './ui/GamePromoModal';
import GameAlbumGallery from './ui/GameAlbumGallery';
import GameCharacterAlbumsPanel from './ui/GameCharacterAlbumsPanel';
import GameTokenPurchasePanel from './ui/GameTokenPurchasePanel';
import GameChatPanel from './ui/GameChatPanel';
import GameLobbyLayer from './ui/GameLobbyLayer';
import GameCharacterGrid from './ui/GameCharacterGrid';
import { GameCharacterWindow, GameRelationshipUpModal, GameAlbumRewardModal } from './ui/GameModals';
import RelationshipBadge from './ui/RelationshipBadge';
import RpDeltaFloat from './ui/RpDeltaFloat';
import SubmissionContractModal from './ui/SubmissionContractModal';
import ContractAddendumModal from './ui/ContractAddendumModal';
import JoiSessionOverlay from './ui/JoiSessionOverlay';
import GameFusionModal from './ui/GameFusionModal';
import GameInventoryModal from './ui/GameInventoryModal';
import { relationshipTierIndex, relationshipTierLabel, nextRpMilestone } from './relationshipLevels';

/** Get titles for the character's current relationship tier */
function getTitlesForCurrentTier(charDoc, characters, gameSettings) {
  const cid = String(charDoc?._id || '');
  const row = (characters || []).find((c) => String(c.characterId) === cid);
  const rp = Number(row?.relationship) || 0;
  const tier = relationshipTierIndex(rp, gameSettings);
  const tierName = relationshipTierLabel(tier).toLowerCase();
  const entry = (charDoc?.titlesByTier || []).find((t) => t.tier === tierName);
  return entry?.titles || [];
}

function flowRpToastToneClass(tone) {
  if (tone === 'positive')
    return 'border-emerald-400/50 bg-emerald-500/20 text-emerald-100 shadow-emerald-950/30';
  if (tone === 'negative') return 'border-red-400/50 bg-red-500/20 text-red-100 shadow-red-950/30';
  if (tone === 'success') return 'border-emerald-400/50 bg-emerald-500/20 text-emerald-100 shadow-emerald-950/30';
  if (tone === 'reward') return 'border-game-popup/60 bg-game-popup/30 text-game-usermsg';
  return 'border-white/15 bg-game-surface text-game-text';
}

/** После интро — авто-старт сессии с этим персонажем (имя в админке, без учёта регистра). */
function normalizeCharName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function isPostIntroAutoStartCharacter(charDoc) {
  const n = normalizeCharName(charDoc?.name);
  return n === 'lina fox' || n === 'lina';
}

function GameEntryCard({ title, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-game-canvas shadow-2xl">
        <div className="border-b border-white/10 bg-game-popup/50 px-4 py-3">
          <h2 className="text-sm font-semibold tracking-tight text-game-text">{title}</h2>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function GameSessionFlow({
  gameSettings,
  onLobbyCharacterLive2dOverlay,
  onFlowLive2dHidden = () => {}
}) {
  const activeLang = useMemo(
    () => (typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('en') ? 'en' : 'ru'),
    []
  );

  const [phase, setPhase] = useState('player_id');
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [introFlow, setIntroFlow] = useState(null);
  /** Активный флоу с персонажа: { flow, characterId, name } */
  const [characterPlay, setCharacterPlay] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsSubview, setSettingsSubview] = useState('main');
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileSubview, setProfileSubview] = useState('main');
  const [kinksCatalog, setKinksCatalog] = useState([]);
  const [kinksSaving, setKinksSaving] = useState(false);
  const [charactersCatalog, setCharactersCatalog] = useState([]);
  /** null = ещё не загружено (показываем загрузку в модалке подарков) */
  const [itemsCatalog, setItemsCatalog] = useState(null);
  const [setsCatalog, setSetsCatalog] = useState([]);
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoKey, setPromoKey] = useState(0);
  const [lobbyView, setLobbyView] = useState('idle');
  const [characterIndex, setCharacterIndex] = useState(0);
  /** После redeem промокода — detail для `game-flow-relationship-up` при закрытии окна промо. */
  const promoPendingRelDetailRef = useRef(null);
  const [relationshipUpOpen, setRelationshipUpOpen] = useState(false);
  const [relationshipUpPayload, setRelationshipUpPayload] = useState(null);
  /** Альбом, разблокированный только по RP (без смены тира). */
  const [albumRpUnlockOpen, setAlbumRpUnlockOpen] = useState(false);
  const [albumRpUnlockPayload, setAlbumRpUnlockPayload] = useState(null);
  /** Тост при изменении RP без смены тира («+N Relationship» / «-N Relationship») */
  const [flowRelationshipToast, setFlowRelationshipToast] = useState(null);
  /** Floating RP delta for session overlay */
  const [rpDeltaFloat, setRpDeltaFloat] = useState({ delta: 0, seq: 0 });
  /** Submission Contract modal (first session with dominant character) */
  const [contractOpen, setContractOpen] = useState(false);
  const [contractPayload, setContractPayload] = useState(null);
  /** JOI session overlay (after Submission Contract accepted) */
  const [joiSessionActive, setJoiSessionActive] = useState(false);
  const [joiSessionContract, setJoiSessionContract] = useState(null);
  /** Contract Addendum modal (tier change with dominant character) */
  const [addendumOpen, setAddendumOpen] = useState(false);
  const [addendumPayload, setAddendumPayload] = useState(null);
  /** Real items catalog */
  const [realItemsCatalog, setRealItemsCatalog] = useState([]);
  /** Inventory viewer */
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [albumViewerSetId, setAlbumViewerSetId] = useState(null);
  /** Если альбом открыли из списка персонажа — по Close вернуться в тот список */
  const [albumViewerReturnCharacterId, setAlbumViewerReturnCharacterId] = useState(null);
  /** Просмотр сетки альбомов персонажа (обложки); отдельно от просмотра конкретного сета */
  const [albumBrowserCharacterId, setAlbumBrowserCharacterId] = useState(null);
  const [chatSelectedId, setChatSelectedId] = useState(null);
  /** Пока идёт ответ AI, чат остаётся смонтированным (в т.ч. на отеле), чтобы не терять запрос. */
  const [chatAiPendingCount, setChatAiPendingCount] = useState(0);
  /** После выхода из флоу / заглушки JOI — один запрос в Grok (как Unity AIChat.Start). */
  const [chatSessionFollowUp, setChatSessionFollowUp] = useState(null);
  const chatFollowUpSeqRef = useRef(0);
  /** Один диспатч follow-up на один выход из персонажного флоу (на случай повторного onExit). */
  const characterPlayRef = useRef(characterPlay);
  characterPlayRef.current = characterPlay;
  const charactersCatalogRef = useRef(charactersCatalog);
  charactersCatalogRef.current = charactersCatalog;
  const characterFlowFollowUpDispatchedRef = useRef(false);
  /** FlowPlayback.registerCharacterFlowExit — выход через exitFlow(), чтобы After Control получил sessionHistory. */
  const characterFlowExitFromPlaybackRef = useRef(null);
  const [chatReplyToast, setChatReplyToast] = useState(null);
  const [tokenShopOpen, setTokenShopOpen] = useState(false);
  const [tokenShopPacks, setTokenShopPacks] = useState([]);
  const [tokenShopLoading, setTokenShopLoading] = useState(false);
  const [tokenShopError, setTokenShopError] = useState(false);

  useEffect(() => {
    const onReset = () => {
      setPhase('player_id');
      setSession(null);
      setIntroFlow(null);
      setCharacterPlay(null);
      setBusy(false);
      setSettingsOpen(false);
      setSettingsSubview('main');
      setPromoOpen(false);
      setLobbyView('idle');
      setCharacterIndex(0);
      setPromoKey((k) => k + 1);
      promoPendingRelDetailRef.current = null;
      setRelationshipUpOpen(false);
      setRelationshipUpPayload(null);
      setAlbumRpUnlockOpen(false);
      setAlbumRpUnlockPayload(null);
      setFlowRelationshipToast(null);
      setAlbumViewerSetId(null);
      setAlbumViewerReturnCharacterId(null);
      setAlbumBrowserCharacterId(null);
      setChatSelectedId(null);
      setChatSessionFollowUp(null);
      setProfileOpen(false);
      setProfileSubview('main');
      setKinksCatalog([]);
      setKinksSaving(false);
      setTokenShopOpen(false);
      setTokenShopPacks([]);
      setTokenShopLoading(false);
      setTokenShopError(false);
      setCharactersCatalog([]);
      setItemsCatalog(null);
      setSetsCatalog([]);
      setJoiSessionActive(false);
      setJoiSessionContract(null);
    };
    window.addEventListener(GAME_SESSION_RESET, onReset);
    return () => window.removeEventListener(GAME_SESSION_RESET, onReset);
  }, []);

  useEffect(() => {
    const onCheatTokens = async (e) => {
      const amount = Number(e?.detail?.amount);
      if (!session?.playerId || !Number.isFinite(amount) || amount <= 0) return;
      const current = Number(session.tokens) || 0;
      const next = current + amount;
      try {
        const res = await patchUserState({ playerId: session.playerId, tokens: next });
        const u = res?.userState;
        setSession((s) => (s ? { ...s, tokens: u?.tokens ?? next } : s));
      } catch (err) {
        console.error(err);
      }
    };
    window.addEventListener(GAME_CHEAT_ADD_TOKENS, onCheatTokens);
    return () => window.removeEventListener(GAME_CHEAT_ADD_TOKENS, onCheatTokens);
  }, [session?.playerId, session?.tokens]);

  const mergeUserStateFromServer = useCallback((u) => {
    if (!u) return;
    setSession((s) =>
      s
        ? {
            ...s,
            nickname: u.nickname ?? s.nickname,
            tokens: u.tokens ?? s.tokens,
            characters: u.characters ?? s.characters,
            sets: u.sets ?? s.sets,
            kinks: u.kinks ?? s.kinks,
            realItems: u.realItems ?? s.realItems,
            inventory: u.inventory ?? s.inventory,
            introFinished: u.introFinished ?? s.introFinished
          }
        : s
    );
  }, []);

  useEffect(() => {
    const onRel = (e) => {
      const d = e.detail || {};
      if (d.tierUp === true) {
        if (!d.fromLabel || !d.toLabel) return;

        // Contract Addendum for dominant characters on tier change
        // (replaces the generic Relationship Up modal)
        const cid = characterPlayRef.current?.characterId;
        let isDominant = false;
        if (cid) {
          const charDoc = charactersCatalogRef.current.find((c) => String(c._id) === String(cid));
          if (charDoc?.preferenceDominant) {
            isDominant = true;
            const toTierName = (d.toLabel || '').toLowerCase();
            const entry = (charDoc.titlesByTier || []).find((t) => t.tier === toTierName);
            setAddendumPayload({
              characterName: charDoc.name || '',
              fromTier: d.fromLabel,
              toTier: d.toLabel,
              newTitles: entry?.titles || []
            });
            setAddendumOpen(true);
          }
        }

        // For non-dominant characters — show the generic Relationship Up modal
        if (!isDominant) {
          setRelationshipUpPayload({
            fromLabel: d.fromLabel,
            toLabel: d.toLabel,
            rewardAlbum: d.rewardAlbum ?? null
          });
          setRelationshipUpOpen(true);
        }
        return;
      }
      if (d.rewardAlbum?.setId) {
        setAlbumRpUnlockPayload({
          setId: String(d.rewardAlbum.setId),
          title: d.rewardAlbum.title,
          photoCount: d.rewardAlbum.photoCount,
          coverImageUrl: d.rewardAlbum.coverImageUrl
        });
        setAlbumRpUnlockOpen(true);
      }
      const delta = Number(d.delta);
      if (Number.isFinite(delta) && delta !== 0) {
        const sign = delta > 0 ? '+' : '';
        setFlowRelationshipToast({
          id: `${Date.now()}-${Math.random()}`,
          message: `${sign}${delta} Relationship`,
          tone: delta > 0 ? 'positive' : 'negative'
        });
        setRpDeltaFloat((prev) => ({ delta, seq: prev.seq + 1 }));
      }
    };
    window.addEventListener('game-flow-relationship-up', onRel);
    return () => window.removeEventListener('game-flow-relationship-up', onRel);
  }, []);

  useEffect(() => {
    if (!flowRelationshipToast) return undefined;
    const t = window.setTimeout(() => setFlowRelationshipToast(null), 3800);
    return () => window.clearTimeout(t);
  }, [flowRelationshipToast]);

  useEffect(() => {
    if (!chatReplyToast) return undefined;
    const t = window.setTimeout(() => setChatReplyToast(null), 6500);
    return () => window.clearTimeout(t);
  }, [chatReplyToast]);

  useEffect(() => {
    const onAfterControl = (e) => {
      const d = e.detail || {};
      const cid = String(d.characterId || '').trim();
      if (!cid) return;
      setChatSessionFollowUp({
        seq: ++chatFollowUpSeqRef.current,
        kind: 'after_control',
        characterId: cid,
        sessionHistory: String(d.sessionHistory || '')
      });
    };
    const onAfterJoi = (e) => {
      const d = e.detail || {};
      const cid = String(d.characterId || '').trim();
      if (!cid) return;
      setChatSessionFollowUp({
        seq: ++chatFollowUpSeqRef.current,
        kind: 'after_joi',
        characterId: cid,
        completedTasks: String(d.completedTasks || ''),
        refusedTasks: String(d.refusedTasks || '')
      });
    };
    window.addEventListener('game-chat-after-control-session', onAfterControl);
    window.addEventListener('game-queue-after-joi-chat-summary', onAfterJoi);
    return () => {
      window.removeEventListener('game-chat-after-control-session', onAfterControl);
      window.removeEventListener('game-queue-after-joi-chat-summary', onAfterJoi);
    };
  }, []);

  const clearChatSessionFollowUp = useCallback(() => {
    flushSync(() => {
      setChatSessionFollowUp(null);
    });
  }, []);

  const registerCharacterFlowExitCallback = useCallback((fn) => {
    characterFlowExitFromPlaybackRef.current = fn;
  }, []);

  useEffect(() => {
    characterFlowFollowUpDispatchedRef.current = false;
  }, [characterPlay?.characterId, characterPlay?.flow?._id]);

  const exitCharacterPlay = useCallback(
    (meta) => {
      const cid = characterPlay?.characterId;
      if (cid && !characterFlowFollowUpDispatchedRef.current) {
        const char = charactersCatalog.find((c) => String(c._id) === String(cid));
        const isSub = Boolean(char?.preferenceSubmissive);
        const isDom = Boolean(char?.preferenceDominant);

        const controlHistoryFromFlow =
          meta != null &&
          typeof meta === 'object' &&
          typeof meta.controlSessionHistory === 'string'
            ? meta.controlSessionHistory
            : null;

        if (isSub) {
          /*
           * Кнопка «Exit» вызывает exitCharacterPlay() без meta (или с SyntheticEvent) —
           * без этого After Control не уходил. Из FlowPlayback приходит { controlSessionHistory }.
           */
          characterFlowFollowUpDispatchedRef.current = true;
          window.dispatchEvent(
            new CustomEvent('game-chat-after-control-session', {
              detail: {
                characterId: cid,
                sessionHistory: controlHistoryFromFlow !== null ? controlHistoryFromFlow : ''
              }
            })
          );
        } else if (isDom && !isSub) {
          characterFlowFollowUpDispatchedRef.current = true;
          /* JOI-задачи в вебе пока не копятся — как в Unity можно отправить пустые списки */
          window.dispatchEvent(
            new CustomEvent('game-queue-after-joi-chat-summary', {
              detail: {
                characterId: cid,
                completedTasks: '',
                refusedTasks: ''
              }
            })
          );
        }
      }
      setCharacterPlay(null);
      setPhase('lobby');
      setLobbyView('characterDetail');
    },
    [characterPlay, charactersCatalog]
  );

  const startCharacterPlayFromProfile = useCallback(
    async (profile, catalogOverride) => {
      const pid = session?.playerId;
      const cid = profile?._id != null ? String(profile._id) : '';
      if (!pid || !cid) return;
      const catalog =
        catalogOverride !== undefined ? catalogOverride : charactersCatalog;
      setBusy(true);
      setPhase('character_flow_loading');
      try {
        const inc = await incrementCharacterSession(pid, cid);
        const u = inc?.userState;
        if (u) mergeUserStateFromServer(u);
        const row = (u?.characters || []).find((c) => String(c.characterId) === cid);
        const sn = Number(row?.sessionNumber) || 0;
        const char = (catalog || []).find((c) => String(c._id) === cid);
        let flowId = null;
        if (sn <= 1) flowId = char?.startFlowId;
        else if (sn <= 5) flowId = char?.mainFlowId;
        else flowId = char?.moreFlowId;
        if (!flowId) {
          setPhase('lobby');
          setLobbyView('characterDetail');
          return;
        }
        const flow = await fetchFlowById(String(flowId));
        setLobbyView('idle');
        setCharacterPlay({ flow, characterId: cid, name: profile?.name || '' });
        setPhase('character_flow');
      } catch (e) {
        console.error(e);
        setPhase('lobby');
        setLobbyView('characterDetail');
      } finally {
        setBusy(false);
      }
    },
    [session?.playerId, charactersCatalog, mergeUserStateFromServer, gameSettings, session?.characters]
  );

  const finalizeIntro = useCallback(async () => {
    const pid = session?.playerId;
    if (pid) {
      try {
        await patchUserState({ playerId: pid, introFinished: true });
      } catch (e) {
        console.error(e);
      }
    }
    setSession((s) => (s ? { ...s, introFinished: true } : s));
    setIntroFlow(null);

    let catalog = charactersCatalog;
    if (!Array.isArray(catalog) || catalog.length === 0) {
      try {
        catalog = await fetchCharacters();
        setCharactersCatalog(Array.isArray(catalog) ? catalog : []);
      } catch (e) {
        console.error(e);
        catalog = [];
      }
    }

    const autoChar = (catalog || []).find((c) => isPostIntroAutoStartCharacter(c));
    if (autoChar) {
      await startCharacterPlayFromProfile(
        {
          _id: String(autoChar._id),
          name: String(autoChar.name || '').trim() || 'Character'
        },
        catalog
      );
    } else {
      setPhase('lobby');
    }
  }, [session?.playerId, charactersCatalog, startCharacterPlayFromProfile]);

  const goIntroOrLobby = useCallback(
    async (playerId, row) => {
      const nick = (row.nickname || '').trim();
      if (!nick) {
        setPhase('nickname');
        return;
      }
      setStoredNickname(nick);
      if (row.introFinished || !gameSettings?.introFlowId) {
        setPhase('lobby');
        return;
      }
      setPhase('intro_loading');
      setBusy(true);
      try {
        const flow = await fetchFlowById(String(gameSettings.introFlowId));
        setIntroFlow(flow);
        setPhase('intro');
      } catch (e) {
        console.error(e);
        try {
          await patchUserState({ playerId, introFinished: true });
        } catch {
          /* ignore */
        }
        setSession((s) => (s ? { ...s, introFinished: true } : s));
        setPhase('lobby');
      } finally {
        setBusy(false);
      }
    },
    [gameSettings?.introFlowId]
  );

  const onPlayerIdSubmit = useCallback(
    async (playerId) => {
      setBusy(true);
      try {
        const data = await lookupUserState(playerId);
        setStoredPlayerId(playerId);
        setSession({
          playerId,
          nickname: data.exists ? data.nickname || '' : '',
          tokens: data.exists ? data.tokens ?? 0 : 0,
          introFinished: data.exists ? Boolean(data.introFinished) : false,
          characters: data.exists ? data.characters || [] : [],
          sets: data.exists ? data.sets || [] : [],
          kinks: data.exists ? data.kinks || [] : [],
          inventory: data.exists ? data.inventory || [] : []
        });
        if (!data.exists) {
          setPhase('nickname');
          return;
        }
        await goIntroOrLobby(playerId, {
          nickname: data.nickname,
          introFinished: data.introFinished,
          tokens: data.tokens
        });
      } catch (e) {
        console.error(e);
      } finally {
        setBusy(false);
      }
    },
    [goIntroOrLobby]
  );

  const onNicknameSubmit = useCallback(
    async (nickname) => {
      if (!session?.playerId) return;
      setBusy(true);
      try {
        const startTok = Number(gameSettings?.startingTokens);
        const res = await patchUserState({
          playerId: session.playerId,
          nickname,
          introFinished: false,
          tokens: Number.isFinite(startTok) ? startTok : 0
        });
        const u = res?.userState;
        const merged = {
          playerId: session.playerId,
          nickname: u?.nickname || nickname,
          tokens: u?.tokens ?? (Number.isFinite(startTok) ? startTok : 0),
          introFinished: Boolean(u?.introFinished),
          characters: u?.characters ?? [],
          sets: u?.sets ?? [],
          kinks: u?.kinks ?? [],
          inventory: u?.inventory ?? []
        };
        setSession(merged);
        setStoredNickname(merged.nickname);
        await goIntroOrLobby(session.playerId, merged);
      } catch (e) {
        console.error(e);
      } finally {
        setBusy(false);
      }
    },
    [session?.playerId, gameSettings?.startingTokens, goIntroOrLobby]
  );

  const canSettings = Boolean(session?.nickname?.trim());

  const openSettings = useCallback(() => {
    setSettingsSubview('main');
    setSettingsOpen(true);
  }, []);

  const openProfile = useCallback(() => {
    setProfileSubview('main');
    setProfileOpen(true);
  }, []);

  const closeProfile = useCallback(() => {
    setProfileOpen(false);
    setProfileSubview('main');
  }, []);

  const persistNickname = useCallback(
    async (name) => {
      if (!session?.playerId) return;
      await patchUserState({ playerId: session.playerId, nickname: name });
      setStoredNickname(name);
      setSession((s) => (s ? { ...s, nickname: name } : s));
    },
    [session?.playerId]
  );

  const openTokenShop = useCallback(() => {
    setTokenShopOpen(true);
    setTokenShopLoading(true);
    setTokenShopError(false);
    fetchCurrencyPacks()
      .then((raw) => {
        const list = (Array.isArray(raw) ? raw : []).filter((p) => p.isActive !== false);
        const sorted = [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setTokenShopPacks(
          sorted.map((p) => ({
            _id: String(p._id),
            name: (p.name || 'Pack').trim() || 'Pack',
            tokens: Number(p.tokens) || 0,
            image: typeof p.image === 'string' ? p.image : '',
            priceDisplay:
              typeof p.price === 'number' && Number.isFinite(p.price)
                ? new Intl.NumberFormat(undefined, {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2
                  }).format(p.price)
                : '—'
          }))
        );
        setTokenShopLoading(false);
      })
      .catch(() => {
        setTokenShopPacks([]);
        setTokenShopError(true);
        setTokenShopLoading(false);
      });
  }, []);

  const closeTokenShop = useCallback(() => {
    setTokenShopOpen(false);
  }, []);

  const handleAlbumUnlockRequest = useCallback(
    async (album) => {
      if (!session?.playerId) return;
      const price = Number(album.price) || 0;
      const bal = Number(session.tokens) || 0;
      if (price > 0 && bal < price) {
        openTokenShop();
        return;
      }
      const setId = String(album.id);
      const owned = new Set((session.sets || []).map(String));
      if (owned.has(setId)) return;
      const newSets = [...owned, setId];
      const newTokens = price > 0 ? bal - price : bal;
      const body =
        price > 0
          ? { playerId: session.playerId, tokens: newTokens, sets: newSets }
          : { playerId: session.playerId, sets: newSets };
      try {
        const res = await patchUserState(body);
        const u = res?.userState;
        setSession((s) =>
          s
            ? {
                ...s,
                tokens: u?.tokens ?? (price > 0 ? newTokens : s.tokens),
                sets: u?.sets ?? newSets
              }
            : s
        );
      } catch (e) {
        console.error(e);
      }
    },
    [session, openTokenShop]
  );

  const onSettingsNicknameSave = useCallback(
    async (name) => {
      try {
        await persistNickname(name);
      } catch (e) {
        console.error(e);
      }
      setSettingsSubview('main');
    },
    [persistNickname]
  );

  const onProfileNicknameSave = useCallback(
    async (name) => {
      try {
        await persistNickname(name);
      } catch (e) {
        console.error(e);
      }
      setProfileSubview('main');
    },
    [persistNickname]
  );

  const handleProfileKinksChange = useCallback(
    async (ids) => {
      if (!session?.playerId) return;
      setKinksSaving(true);
      try {
        const res = await patchUserState({ playerId: session.playerId, kinks: ids });
        const u = res?.userState;
        setSession((s) => (s ? { ...s, kinks: u?.kinks ?? ids } : s));
      } catch (e) {
        console.error(e);
      } finally {
        setKinksSaving(false);
      }
    },
    [session?.playerId]
  );

  const [realItemsSaving, setRealItemsSaving] = useState(false);
  const handleProfileRealItemsChange = useCallback(
    async (ids) => {
      if (!session?.playerId) return;
      setRealItemsSaving(true);
      try {
        const res = await patchUserState({ playerId: session.playerId, realItems: ids });
        const u = res?.userState;
        setSession((s) => (s ? { ...s, realItems: u?.realItems ?? ids } : s));
      } catch (e) {
        console.error(e);
      } finally {
        setRealItemsSaving(false);
      }
    },
    [session?.playerId]
  );

  useEffect(() => {
    if (!session?.playerId) return;
    const loadCatalog =
      phase === 'lobby' ||
      phase === 'intro' ||
      phase === 'intro_loading' ||
      phase === 'character_flow' ||
      phase === 'character_flow_loading';
    if (!loadCatalog) return;
    let cancel = false;
    (async () => {
      try {
        const [chars, sts, kinks, rItems] = await Promise.all([
          fetchCharacters(),
          fetchSets(),
          fetchKinks().catch(() => []),
          fetchRealItems().catch(() => [])
        ]);
        if (!cancel) {
          setCharactersCatalog(chars);
          setSetsCatalog(sts);
          setKinksCatalog(Array.isArray(kinks) ? kinks : []);
          setRealItemsCatalog(Array.isArray(rItems) ? rItems : []);
        }
      } catch (e) {
        console.error(e);
        if (!cancel) {
          setCharactersCatalog([]);
          setSetsCatalog([]);
          setKinksCatalog([]);
          setRealItemsCatalog([]);
        }
      }
    })();
    return () => {
      cancel = true;
    };
  }, [phase, session?.playerId]);

  useEffect(() => {
    if (!session?.playerId) {
      setItemsCatalog(null);
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const items = await fetchItems();
        if (!cancel) setItemsCatalog(Array.isArray(items) ? items : []);
      } catch (e) {
        console.error(e);
        if (!cancel) setItemsCatalog([]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [session?.playerId]);

  const flowGiftCatalog = useMemo(() => {
    if (itemsCatalog == null) return null;
    return itemsCatalog
      .filter((it) => String(it.type || '') === 'Gift')
      .map((it) => ({
        id: String(it._id),
        name: String(it.name || '').trim() || 'Gift',
        price: Number(it.cost) || 0,
        icon: String(it.icon || '').trim(),
        instruction: String(it.instruction || '').trim()
      }));
  }, [itemsCatalog]);

  const flowItemCatalog = useMemo(() => {
    if (itemsCatalog == null) return [];
    return itemsCatalog
      .filter((it) => String(it.type || '') !== 'Gift')
      .map((it) => ({
        id: String(it._id),
        name: String(it.name || '').trim() || 'Item',
        price: Number(it.cost) || 0,
        emoji: String(it.icon || '').trim() || '📦',
        itemType: String(it.type || '')
      }));
  }, [itemsCatalog]);

  const lobbyProfiles = useMemo(
    () => buildLobbyCharacterProfiles(charactersCatalog, session?.characters || [], gameSettings),
    [charactersCatalog, session?.characters, gameSettings]
  );

  /** Снять фоновый Live2D только во время интро и персонажного флоу (два Cubism на экране ломают рендер). */
  const suspendGamePageLive2d = useMemo(
    () =>
      phase === 'intro' ||
      phase === 'intro_loading' ||
      phase === 'character_flow' ||
      phase === 'character_flow_loading',
    [phase]
  );

  useLayoutEffect(() => {
    if (typeof onLobbyCharacterLive2dOverlay !== 'function') return undefined;
    onLobbyCharacterLive2dOverlay(suspendGamePageLive2d);
    return () => {
      onLobbyCharacterLive2dOverlay(false);
    };
  }, [suspendGamePageLive2d, onLobbyCharacterLive2dOverlay]);

  useEffect(() => {
    if (suspendGamePageLive2d) setChatReplyToast(null);
  }, [suspendGamePageLive2d]);

  const chatContacts = useMemo(
    () =>
      lobbyProfiles.map((p) => ({
        id: String(p._id),
        name: p.name,
        preview:
          Number(p.sessionNumber) >= 1 ? 'Ready to chat' : 'Finish first session to unlock',
        sessionNumber: p.sessionNumber,
        avatarUrl: p.avatarChatUrl || undefined
      })),
    [lobbyProfiles]
  );

  const chatEffectiveId = useMemo(() => {
    if (!chatContacts.length) return null;
    if (
      chatSelectedId &&
      chatContacts.some((c) => String(c.id) === String(chatSelectedId))
    ) {
      return String(chatSelectedId);
    }
    return String(chatContacts[0].id);
  }, [chatContacts, chatSelectedId]);

  const onChatReplyNotification = useCallback(
    (payload) => {
      if (suspendGamePageLive2d) return;
      const { characterId, characterName, preview } = payload || {};
      if (!characterId) return;
      setChatReplyToast({
        id: `${Date.now()}-${characterId}`,
        characterId: String(characterId),
        title: String(characterName || 'Chat').trim() || 'Chat',
        preview: String(preview || '').trim() || '…'
      });
    },
    [suspendGamePageLive2d]
  );

  const onPendingAiDelta = useCallback((delta) => {
    const d = Number(delta) || 0;
    if (!d) return;
    setChatAiPendingCount((c) => Math.max(0, c + d));
  }, []);

  const albumBrowserAlbums = useMemo(() => {
    if (!albumBrowserCharacterId) return [];
    const cid = String(albumBrowserCharacterId);
    const unlocked = new Set((session?.sets || []).map(String));
    return (setsCatalog || [])
      .filter((s) => String(s.characterId?._id || s.characterId) === cid)
      .map((s) => ({
        id: String(s._id),
        title: (s.name || 'Album').trim() || 'Album',
        photoCount: Array.isArray(s.photos) ? s.photos.length : 0,
        coverImageUrl: typeof s.coverImage === 'string' ? s.coverImage.trim() : '',
        owned: unlocked.has(String(s._id)),
        price: Number(s.price) || 0
      }));
  }, [albumBrowserCharacterId, setsCatalog, session?.sets]);

  const albumBrowserTitle = useMemo(() => {
    if (!albumBrowserCharacterId) return 'Albums';
    const name = charactersCatalog.find((c) => String(c._id) === String(albumBrowserCharacterId))?.name;
    return name ? `Albums — ${name}` : 'Albums';
  }, [albumBrowserCharacterId, charactersCatalog]);

  const albumViewerTitle = useMemo(() => {
    if (!albumViewerSetId) return 'Album';
    const s = setsCatalog.find((x) => String(x._id) === String(albumViewerSetId));
    const name = (s?.name || '').trim();
    return name || 'Album';
  }, [albumViewerSetId, setsCatalog]);

  const closeAlbumViewer = useCallback(() => {
    const back = albumViewerReturnCharacterId;
    setAlbumViewerSetId(null);
    setAlbumViewerReturnCharacterId(null);
    if (back) setAlbumBrowserCharacterId(back);
  }, [albumViewerReturnCharacterId]);

  const activeLobbyTab = useMemo(() => {
    if (lobbyView === 'fusion') return 'fusion';
    if (lobbyView === 'chat') return 'chat';
    if (
      lobbyView === 'characterGrid' ||
      lobbyView === 'characterDetail' ||
      albumBrowserCharacterId ||
      albumViewerSetId
    ) {
      return 'characters';
    }
    return 'hotel';
  }, [lobbyView, albumBrowserCharacterId, albumViewerSetId]);

  const handleSelectLobbyTab = useCallback(
    (tab) => {
      if (tab === 'hotel') {
        setLobbyView('idle');
        setAlbumBrowserCharacterId(null);
        setAlbumViewerSetId(null);
        setAlbumViewerReturnCharacterId(null);
        return;
      }
      if (tab === 'characters') {
        setAlbumBrowserCharacterId(null);
        setAlbumViewerSetId(null);
        setAlbumViewerReturnCharacterId(null);
        if (lobbyView === 'characterDetail') {
          setLobbyView('characterGrid');
          return;
        }
        if (lobbyView === 'characterGrid') {
          return;
        }
        setCharacterIndex(0);
        setLobbyView('characterGrid');
        return;
      }
      if (tab === 'chat') {
        setAlbumBrowserCharacterId(null);
        setAlbumViewerSetId(null);
        setAlbumViewerReturnCharacterId(null);
        if (chatContacts.length > 0) {
          setChatSelectedId(String(chatContacts[0].id));
        }
        setLobbyView('chat');
        return;
      }
      if (tab === 'fusion') {
        setAlbumBrowserCharacterId(null);
        setAlbumViewerSetId(null);
        setAlbumViewerReturnCharacterId(null);
        setLobbyView('fusion');
      }
    },
    [chatContacts, lobbyView]
  );

  const handleLobbyBackdrop = useCallback(() => {
    if (lobbyView === 'chat' || lobbyView === 'fusion') {
      setLobbyView('idle');
      return;
    }
    if (lobbyView === 'characterDetail') {
      setLobbyView('characterGrid');
      return;
    }
    if (lobbyView === 'characterGrid') {
      setLobbyView('idle');
    }
  }, [lobbyView]);

  const closePromo = useCallback(() => {
    setPromoOpen(false);
    const detail = promoPendingRelDetailRef.current;
    promoPendingRelDetailRef.current = null;
    if (detail) {
      window.dispatchEvent(new CustomEvent('game-flow-relationship-up', { detail }));
    }
  }, []);

  const handlePromoRedeem = useCallback(
    async (code) => {
      if (!session?.playerId) throw new Error('No session');
      const data = await redeemPromo(session.playerId, code);
      let setsList = setsCatalog;
      if (!setsList.length) {
        try {
          setsList = await fetchSets();
          setSetsCatalog(setsList);
        } catch {
          setsList = [];
        }
      }
      const bonus = Math.floor(Number(data.relationshipBonus) || 0);
      const { characters: newChars, sets: mergedSetIds, prevRp, nextRp: newRp, newlyUnlockedIds } =
        applyRpDeltaToCharactersAndSets(
          session.characters || [],
          session.sets || [],
          data.characterId,
          bonus,
          setsList
        );
      const detail = buildRelationshipRewardDispatchDetail(
        prevRp,
        newRp,
        gameSettings,
        newlyUnlockedIds,
        setsList
      );
      promoPendingRelDetailRef.current =
        detail.tierUp || detail.rewardAlbum?.setId || (Number.isFinite(detail.delta) && detail.delta !== 0)
          ? detail
          : null;
      const res = await patchUserState({
        playerId: session.playerId,
        characters: newChars,
        sets: mergedSetIds
      });
      const u = res?.userState;
      setSession((s) => {
        if (!s) return s;
        return {
          ...s,
          characters: u?.characters ?? newChars,
          sets: u?.sets ?? mergedSetIds,
          tokens: u?.tokens ?? s.tokens,
          kinks: u?.kinks ?? s.kinks
        };
      });
      const name =
        charactersCatalog.find((c) => String(c._id) === String(data.characterId))?.name || 'Character';
      return { characterName: name, bonus: data.relationshipBonus };
    },
    [session, setsCatalog, charactersCatalog, gameSettings]
  );

  return (
    <>
      {phase === 'player_id' ? (
        <GameEntryCard title="Welcome">
          <GamePlayerIdModal
            initialPlayerId={getStoredPlayerId()}
            busy={busy}
            onSubmit={onPlayerIdSubmit}
          />
        </GameEntryCard>
      ) : null}

      {phase === 'nickname' ? (
        <GameEntryCard title="Choose a nickname">
          <GameNicknameModal
            initialNickname={getStoredNickname('')}
            busy={busy}
            onSave={onNicknameSubmit}
            onBack={() => {
              setSession(null);
              setPhase('player_id');
            }}
          />
        </GameEntryCard>
      ) : null}

      {phase === 'intro_loading' ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70">
          <p className="text-sm text-game-muted">Loading intro…</p>
        </div>
      ) : null}

      {phase === 'intro' && introFlow ? (
        <>
          <FlowPlayback
            flow={introFlow}
            activeLang={activeLang}
            onExit={finalizeIntro}
            playerTokens={session?.tokens ?? 0}
            onInsufficientTokens={openTokenShop}
            gameSettings={gameSettings}
            playerId={session?.playerId || ''}
            session={session}
            playCharacterId={null}
            onUserStateUpdated={mergeUserStateFromServer}
            onLive2dPolicy={onFlowLive2dHidden}
            characterName=""
            flowGiftCatalog={flowGiftCatalog}
            setsCatalog={setsCatalog}
          />
          <div className="pointer-events-none fixed inset-x-0 top-0 z-40 flex justify-end gap-2 p-3">
            <div className="pointer-events-auto flex items-center gap-2">
              {canSettings ? (
                <motion.button
                  type="button"
                  transition={snappyTween}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={openSettings}
                  className="rounded-xl border border-white/15 bg-game-surface/90 px-3 py-2 text-xs font-semibold text-game-text backdrop-blur-md"
                >
                  Settings
                </motion.button>
              ) : null}
              <motion.button
                type="button"
                transition={snappyTween}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={finalizeIntro}
                className="rounded-xl border border-game-accent/40 bg-game-accent/20 px-3 py-2 text-xs font-bold text-game-accentText backdrop-blur-md"
              >
                Skip
              </motion.button>
            </div>
          </div>
        </>
      ) : null}

      {phase === 'character_flow_loading' ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70">
          <p className="text-sm text-game-muted">Loading session…</p>
        </div>
      ) : null}

      {phase === 'character_flow' && characterPlay?.flow ? (
        <>
          <FlowPlayback
            key={characterPlay.characterId}
            flow={characterPlay.flow}
            activeLang={activeLang}
            onExit={exitCharacterPlay}
            playerTokens={session?.tokens ?? 0}
            onInsufficientTokens={openTokenShop}
            gameSettings={gameSettings}
            playerId={session?.playerId || ''}
            session={session}
            playCharacterId={characterPlay.characterId}
            onUserStateUpdated={mergeUserStateFromServer}
            onLive2dPolicy={onFlowLive2dHidden}
            characterName={characterPlay.name}
            flowGiftCatalog={flowGiftCatalog}
            flowItemCatalog={flowItemCatalog}
            trackControlSessionSummary={Boolean(
              charactersCatalog.find((c) => String(c._id) === String(characterPlay.characterId))
                ?.preferenceSubmissive
            )}
            summaryCharacterName={characterPlay.name}
            summaryPlayerNickname={session?.nickname || ''}
            registerCharacterFlowExit={registerCharacterFlowExitCallback}
            setsCatalog={setsCatalog}
            onJoiSession={() => {
              const cid = characterPlay?.characterId;
              const char = charactersCatalog.find((c) => String(c._id) === String(cid));
              if (!char) return;
              const tierTitles = getTitlesForCurrentTier(char, session?.characters || [], gameSettings);
              setContractPayload({
                characterName: char.name || characterPlay?.name || '',
                tierTitles
              });
              setContractOpen(true);
            }}
          />
          <div className="pointer-events-none fixed inset-x-0 top-0 z-40 flex items-start justify-between gap-2 p-3">
            {(() => {
              const cid = characterPlay?.characterId;
              const row = (session?.characters || []).find((c) => String(c.characterId) === String(cid));
              const rp = Number(row?.relationship) || 0;
              const tier = relationshipTierIndex(rp, gameSettings);
              const milestone = nextRpMilestone(rp, tier, gameSettings);
              return (
                <div className="pointer-events-auto flex items-center gap-1.5">
                  <RelationshipBadge rpCurrent={rp} rpNext={Math.max(rp + 1, milestone)} tierLabel={relationshipTierLabel(tier)} />
                  <RpDeltaFloat delta={rpDeltaFloat.delta} seq={rpDeltaFloat.seq} />
                </div>
              );
            })()}
            <div className="pointer-events-auto flex items-center gap-2">
              {canSettings ? (
                <motion.button
                  type="button"
                  transition={snappyTween}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={openSettings}
                  className="rounded-xl border border-white/15 bg-game-surface/90 px-3 py-2 text-xs font-semibold text-game-text backdrop-blur-md"
                >
                  Settings
                </motion.button>
              ) : null}
              <motion.button
                type="button"
                transition={snappyTween}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  const leave = characterFlowExitFromPlaybackRef.current;
                  if (typeof leave === 'function') leave();
                  else exitCharacterPlay();
                }}
                className="rounded-xl border border-game-accent/40 bg-game-accent/20 px-3 py-2 text-xs font-bold text-game-accentText backdrop-blur-md"
              >
                Exit
              </motion.button>
            </div>
          </div>
        </>
      ) : null}

      {phase === 'lobby' && session ? (
        <div className="absolute inset-0 z-20 flex min-h-0 flex-col">
          <div className="relative z-[50] shrink-0">
            <GameTopBar
              nickname={session.nickname || ''}
              tokens={session.tokens ?? 0}
              onOpenProfile={openProfile}
              onOpenTokenShop={openTokenShop}
              onOpenSettings={openSettings}
              activeLobbyTab={activeLobbyTab}
              onSelectLobbyTab={handleSelectLobbyTab}
              onOpenPromo={() => {
                setPromoKey((k) => k + 1);
                setPromoOpen(true);
              }}
              onOpenInventory={() => setInventoryOpen(true)}
            />
          </div>
          <div className="relative min-h-0 flex-1">
            {lobbyView !== 'idle' ? (
              <GameLobbyLayer
                onBackdropClick={handleLobbyBackdrop}
                contentBleed={lobbyView === 'characterDetail'}
              >
                <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
                {lobbyView === 'characterGrid' ? (
                  <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-hidden">
                    <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[rgba(237,232,229,0.14)] bg-game-chatHeader/90 px-3 py-2.5 backdrop-blur-md">
                      <span className="text-sm font-semibold text-game-text">Characters</span>
                      <button
                        type="button"
                        onClick={() => setLobbyView('idle')}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xl leading-none text-game-muted transition-colors hover:bg-white/10 hover:text-game-text"
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>
                    <div className="vr-scroll-y min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
                      {lobbyProfiles.length === 0 ? (
                        <p className="text-center text-sm text-game-muted">No characters loaded.</p>
                      ) : (
                        <GameCharacterGrid
                          profiles={lobbyProfiles}
                          onSelectIndex={(i) => {
                            setCharacterIndex(i);
                            setLobbyView('characterDetail');
                          }}
                        />
                      )}
                    </div>
                  </div>
                ) : null}
                {lobbyView === 'characterDetail' ? (
                  <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-hidden">
                    <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[rgba(237,232,229,0.14)] bg-game-chatHeader/90 px-3 py-2.5 backdrop-blur-md">
                      <nav className="flex min-w-0 flex-1 items-center gap-1.5 text-sm" aria-label="Character navigation">
                        <button
                          type="button"
                          onClick={() => setLobbyView('characterGrid')}
                          className="shrink-0 font-medium text-game-muted transition-colors hover:text-game-text"
                        >
                          Characters
                        </button>
                        <span className="shrink-0 text-game-muted" aria-hidden>
                          /
                        </span>
                        <span className="min-w-0 truncate font-semibold text-game-text">
                          {lobbyProfiles[characterIndex]?.name || '—'}
                        </span>
                      </nav>
                      <button
                        type="button"
                        onClick={() => setLobbyView('idle')}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xl leading-none text-game-muted transition-colors hover:bg-white/10 hover:text-game-text"
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>
                    <div className="relative min-h-0 flex-1 overflow-visible">
                      {lobbyProfiles.length === 0 ? (
                        <p className="p-6 text-center text-sm text-game-muted">No characters loaded.</p>
                      ) : (
                        <GameCharacterWindow
                          profiles={lobbyProfiles}
                          characterIndex={characterIndex}
                          onCharacterIndexChange={setCharacterIndex}
                          onChatClick={(p) => {
                            setChatSelectedId(String(p._id));
                            setLobbyView('chat');
                          }}
                          onPhotosClick={(p) => {
                            const cid = String(p._id);
                            const hasAlbums = setsCatalog.some(
                              (s) => String(s.characterId?._id || s.characterId) === cid
                            );
                            if (!hasAlbums) return;
                            setLobbyView('idle');
                            setAlbumBrowserCharacterId(cid);
                          }}
                          onPlayClick={(p) => startCharacterPlayFromProfile(p)}
                        />
                      )}
                    </div>
                  </div>
                ) : null}
                {chatContacts.length > 0 && (lobbyView !== 'idle' || chatAiPendingCount > 0) ? (
                  <div
                    className={
                      lobbyView === 'chat'
                        ? 'relative z-20 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden'
                        : 'pointer-events-none absolute inset-0 z-0 flex min-h-0 min-w-0 flex-col overflow-hidden opacity-0'
                    }
                    aria-hidden={lobbyView !== 'chat'}
                  >
                    <GameChatPanel
                      embedded={lobbyView === 'chat'}
                      onClose={lobbyView === 'chat' ? () => setLobbyView('idle') : undefined}
                      chatUiVisible={lobbyView === 'chat'}
                      viewedCharacterId={chatEffectiveId}
                      sessionFollowUpRequest={chatSessionFollowUp}
                      onSessionFollowUpConsumed={clearChatSessionFollowUp}
                      onChatReplyNotification={onChatReplyNotification}
                      onPendingAiDelta={onPendingAiDelta}
                      gameContacts={chatContacts}
                      gameSelectedId={chatEffectiveId}
                      onGameSelectContact={(id) => setChatSelectedId(id)}
                      playerId={session.playerId}
                      playerNickname={session.nickname || ''}
                      session={session}
                      gameSettings={gameSettings}
                      kinksCatalog={kinksCatalog}
                      charactersCatalog={charactersCatalog}
                      setsCatalog={setsCatalog}
                      giftCatalog={flowGiftCatalog}
                      onUserStateUpdated={mergeUserStateFromServer}
                      onInsufficientTokens={openTokenShop}
                      onChatAlbumUnlock={handleAlbumUnlockRequest}
                      onChatAlbumOpen={(setId) => {
                        setAlbumViewerReturnCharacterId(chatEffectiveId);
                        setAlbumViewerSetId(setId);
                      }}
                    />
                  </div>
                ) : null}
                {lobbyView === 'chat' && chatContacts.length === 0 ? (
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[rgba(237,232,229,0.14)] bg-game-chatHeader/90 px-3 py-2.5 backdrop-blur-md">
                      <span className="text-sm font-semibold text-game-text">Chat</span>
                      <button
                        type="button"
                        onClick={() => setLobbyView('idle')}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xl leading-none text-game-muted transition-colors hover:bg-white/10 hover:text-game-text"
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>
                    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 p-6">
                      <p className="max-w-sm text-center text-sm text-game-muted">
                        Complete a session with a character to unlock chat.
                      </p>
                    </div>
                  </div>
                ) : null}
                {lobbyView === 'fusion' ? (
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[rgba(237,232,229,0.14)] bg-game-chatHeader/90 px-3 py-2.5 backdrop-blur-md">
                      <span className="text-sm font-semibold text-game-text">Fusion Room</span>
                      <button
                        type="button"
                        onClick={() => setLobbyView('idle')}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xl leading-none text-game-muted transition-colors hover:bg-white/10 hover:text-game-text"
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>
                    <div className="vr-scroll-y min-h-0 flex-1">
                      <GameFusionModal
                        fusionEventDate={gameSettings?.fusionEventDate}
                        fusionRequirements={gameSettings?.fusionRequirements || []}
                        charactersCatalog={charactersCatalog}
                      />
                    </div>
                  </div>
                ) : null}
                </div>
              </GameLobbyLayer>
            ) : (
              <div className="vr-scroll-y pointer-events-auto min-h-0 flex-1" aria-hidden />
            )}
          </div>
        </div>
      ) : null}

      {promoOpen ? (
        <GamePreviewShell title="Apply promo code" onClose={closePromo}>
          <GamePromoModal key={promoKey} onClose={closePromo} onRedeem={handlePromoRedeem} />
        </GamePreviewShell>
      ) : null}

      <div
        className="pointer-events-none fixed inset-x-0 top-0 z-[85] flex justify-end pt-[50px] pr-3 pl-3 sm:pr-4 sm:pl-4"
        aria-live="polite"
      >
        <div className="flex max-w-[min(22rem,calc(100vw-1.5rem))] flex-col items-end gap-2">
          <AnimatePresence mode="sync">
            {flowRelationshipToast ? (
              <motion.div
                key={flowRelationshipToast.id}
                layout
                initial={{ opacity: 0, x: 28, scale: 0.94 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 16 }}
                transition={{ type: 'spring', damping: 20, stiffness: 320 }}
                className={`pointer-events-auto w-full rounded-2xl border px-4 py-3 text-sm font-semibold leading-snug shadow-lg ${flowRpToastToneClass(
                  flowRelationshipToast.tone
                )}`}
              >
                {flowRelationshipToast.message}
              </motion.div>
            ) : null}
          </AnimatePresence>
          <AnimatePresence mode="sync">
            {chatReplyToast ? (
              <motion.button
                key={chatReplyToast.id}
                type="button"
                layout
                initial={{ opacity: 0, x: 28, scale: 0.94 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 16 }}
                transition={{ type: 'spring', damping: 20, stiffness: 320 }}
                onClick={() => {
                  setChatSelectedId(chatReplyToast.characterId);
                  setLobbyView('chat');
                  setChatReplyToast(null);
                }}
                className="pointer-events-auto w-full rounded-2xl border border-game-popup/50 bg-game-popup/35 px-4 py-3 text-left text-sm leading-snug text-game-usermsg shadow-lg backdrop-blur-md transition-colors hover:bg-game-popup/45"
              >
                <span className="block text-[10px] font-bold uppercase tracking-wide text-game-muted">
                  New message
                </span>
                <span className="mt-0.5 block font-semibold text-game-text">{chatReplyToast.title}</span>
                <span className="mt-1 line-clamp-3 text-game-body">{chatReplyToast.preview}</span>
              </motion.button>
            ) : null}
          </AnimatePresence>
        </div>
      </div>

      {relationshipUpOpen && relationshipUpPayload ? (
        <GamePreviewShell
          title="Relationship"
          overlayZClass="z-[75]"
          onClose={() => {
            setRelationshipUpOpen(false);
            setRelationshipUpPayload(null);
          }}
        >
          <GameRelationshipUpModal
            payload={relationshipUpPayload}
            onOpenAlbum={() => {
              const sid = relationshipUpPayload?.rewardAlbum?.setId;
              if (sid) {
                setAlbumBrowserCharacterId(null);
                setAlbumViewerSetId(String(sid));
                setRelationshipUpOpen(false);
                setRelationshipUpPayload(null);
              }
            }}
          />
        </GamePreviewShell>
      ) : null}

      {albumRpUnlockOpen && albumRpUnlockPayload ? (
        <GamePreviewShell
          title="Album unlocked"
          overlayZClass="z-[75]"
          onClose={() => {
            setAlbumRpUnlockOpen(false);
            setAlbumRpUnlockPayload(null);
          }}
        >
          <GameAlbumRewardModal
            payload={albumRpUnlockPayload}
            onOpen={() => {
              const sid = albumRpUnlockPayload?.setId;
              if (sid) {
                setAlbumBrowserCharacterId(null);
                setAlbumViewerSetId(String(sid));
                setAlbumRpUnlockOpen(false);
                setAlbumRpUnlockPayload(null);
              }
            }}
          />
        </GamePreviewShell>
      ) : null}

      {inventoryOpen ? (
        <GamePreviewShell title="Inventory" onClose={() => setInventoryOpen(false)} wide>
          <GameInventoryModal
            itemsCatalog={itemsCatalog || []}
            ownedItemIds={session?.inventory || []}
            playerTokens={session?.tokens ?? 0}
            onPurchase={async (item) => {
              if (!session?.playerId) return;
              const cost = Number(item.cost) || 0;
              const currentTokens = Number(session.tokens) || 0;
              if (cost > currentTokens) return;
              const newInventory = [...(session.inventory || []), String(item._id)];
              const newTokens = currentTokens - cost;
              try {
                const res = await patchUserState({
                  playerId: session.playerId,
                  inventory: newInventory,
                  tokens: newTokens
                });
                if (res?.userState) mergeUserStateFromServer(res.userState);
              } catch (e) { console.error(e); }
            }}
          />
        </GamePreviewShell>
      ) : null}

      {contractOpen && contractPayload ? (
        <SubmissionContractModal
          characterName={contractPayload.characterName}
          nickname={session?.nickname || ''}
          tierTitles={contractPayload.tierTitles}
          kinksCatalog={kinksCatalog}
          selectedKinkIds={session?.kinks || []}
          onKinksChange={async (ids) => {
            if (!session?.playerId) return;
            try {
              const res = await patchUserState({ playerId: session.playerId, kinks: ids });
              if (res?.userState) mergeUserStateFromServer(res.userState);
            } catch (e) { console.error(e); }
          }}
          realItemsCatalog={realItemsCatalog}
          selectedRealItemIds={session?.realItems || []}
          onRealItemsChange={async (ids) => {
            if (!session?.playerId) return;
            try {
              const res = await patchUserState({ playerId: session.playerId, realItems: ids });
              if (res?.userState) mergeUserStateFromServer(res.userState);
            } catch (e) { console.error(e); }
          }}
          onAccept={() => {
            setContractOpen(false);
            setContractPayload(null);
            // Start JOI session after contract accepted
            setJoiSessionContract({
              playerKinkIds: session?.kinks || [],
              playerRealItemIds: session?.realItems || [],
              targetRating: 'Normal'
            });
            setJoiSessionActive(true);
          }}
        />
      ) : null}

      {joiSessionActive && joiSessionContract ? (
        <JoiSessionOverlay
          contract={joiSessionContract}
          activeLang={activeLang}
          onFinish={(result) => {
            setJoiSessionActive(false);
            setJoiSessionContract(null);
            // Dispatch after-JOI chat summary
            const cid = characterPlay?.characterId;
            if (cid) {
              window.dispatchEvent(
                new CustomEvent('game-queue-after-joi-chat-summary', {
                  detail: {
                    characterId: cid,
                    completedTasks: (result.completedTasks || []).join('; '),
                    refusedTasks: (result.refusedTasks || []).join('; ')
                  }
                })
              );
            }
          }}
        />
      ) : null}

      {addendumOpen && addendumPayload ? (
        <ContractAddendumModal
          characterName={addendumPayload.characterName}
          fromTier={addendumPayload.fromTier}
          toTier={addendumPayload.toTier}
          newTitles={addendumPayload.newTitles}
          onAccept={() => {
            setAddendumOpen(false);
            setAddendumPayload(null);
          }}
        />
      ) : null}

      {albumBrowserCharacterId ? (
        <GamePreviewShell title={albumBrowserTitle} onClose={() => setAlbumBrowserCharacterId(null)} wide>
          <GameCharacterAlbumsPanel
            albums={albumBrowserAlbums}
            onOpenAlbum={(setId) => {
              const cid = albumBrowserCharacterId;
              setAlbumViewerReturnCharacterId(cid ? String(cid) : null);
              setAlbumBrowserCharacterId(null);
              setAlbumViewerSetId(setId);
            }}
            onUnlockAlbum={handleAlbumUnlockRequest}
          />
        </GamePreviewShell>
      ) : null}

      {albumViewerSetId ? (
        <GamePreviewShell title={albumViewerTitle} onClose={closeAlbumViewer} wide>
          <GameAlbumGallery setId={albumViewerSetId} />
        </GamePreviewShell>
      ) : null}

      {profileOpen ? (
        <GamePreviewShell
          title={
            profileSubview === 'nickname'
              ? 'Nickname'
              : <span className="inline-flex items-center gap-1.5"><svg className="inline h-4 w-4 text-game-muted" viewBox="0 0 24 24" fill="none" aria-hidden><path d="M19 21v-1.5a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4V21M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg> Profile</span>
          }
          onClose={closeProfile}
        >
          {profileSubview === 'main' ? (
            <GameProfileModal
              profileDisplayName={session?.nickname ?? ''}
              onOpenNickname={() => setProfileSubview('nickname')}
              kinksCatalog={kinksCatalog}
              selectedKinkIds={session?.kinks || []}
              onKinksChange={handleProfileKinksChange}
              kinksSaving={kinksSaving}
              realItemsCatalog={realItemsCatalog}
              selectedRealItemIds={session?.realItems || []}
              onRealItemsChange={handleProfileRealItemsChange}
              realItemsSaving={realItemsSaving}
            />
          ) : (
            <GameNicknameModal
              initialNickname={session?.nickname || getStoredNickname('')}
              onSave={onProfileNicknameSave}
              onBack={() => setProfileSubview('main')}
            />
          )}
        </GamePreviewShell>
      ) : null}

      {settingsOpen ? (
        <GamePreviewShell
          title={settingsSubview === 'nickname' ? 'Nickname' : 'Settings'}
          onClose={() => {
            setSettingsOpen(false);
            setSettingsSubview('main');
          }}
        >
          {settingsSubview === 'main' ? (
            <GameSettingsModal
              profileDisplayName={session?.nickname ?? ''}
              serverPlayerId={session?.playerId ?? ''}
              onPurgeComplete={() => {
                setSettingsOpen(false);
                setSettingsSubview('main');
              }}
              onOpenNickname={() => setSettingsSubview('nickname')}
            />
          ) : (
            <GameNicknameModal
              initialNickname={session?.nickname || getStoredNickname('')}
              onSave={onSettingsNicknameSave}
              onBack={() => setSettingsSubview('main')}
            />
          )}
        </GamePreviewShell>
      ) : null}

      {tokenShopOpen ? (
        <GamePreviewShell
          title="Buy tokens"
          onClose={closeTokenShop}
          wide
          overlayZClass="z-[80]"
        >
          <GameTokenPurchasePanel
            packs={tokenShopPacks}
            loading={tokenShopLoading}
            error={tokenShopError}
          />
        </GamePreviewShell>
      ) : null}
    </>
  );
}
